/**
 * Las decisiones del usuario durante el túnel de alta.
 *
 * Dos ideas sostienen todo el archivo:
 *
 * - **Lo que el backend contesta es la verdad; acá sólo vive lo que el usuario decidió.** Las
 *   cuentas, las propuestas y los presupuestos se vuelven a pedir en cada paso y no se guardan:
 *   entre que la pantalla los leyó y el usuario los confirma, alguien de la agencia pudo conectar
 *   otra plataforma o armar una línea. Guardar una copia sería inventar un mundo paralelo.
 * - **Reconciliar, no reiniciar.** Cuando los datos del servidor cambian, las decisiones que siguen
 *   teniendo sentido se conservan y las que quedaron sin objeto se descartan. Perder lo que el
 *   usuario venía marcando porque una cuenta desapareció sería peor que el problema.
 */

/**
 * Qué viene ya tildado y qué espera un gesto del usuario.
 *
 * **NADA viene tildado: la cartera la elige el usuario** (decisión de fede, 2026-09-22). Lo que
 * cerró la discusión fue mirar la pantalla con una cartera real: «vas a tener gente que ni va a
 * mirar y va a apretar continuar, pimba. Y te va a mandar a procesar un montón de cuentas que a mí
 * no me interesan en absoluto». Elegir por el usuario tiene un costo asimétrico — lo que entra de
 * más produce clientes y líneas REALES que después hay que borrar a mano, mientras que lo que falta
 * se agrega con un clic desde la misma pantalla.
 *
 * El criterio anterior era el TAMAÑO DEL GRUPO: un cliente de una sola cuenta nacía tildado porque
 * no hay ningún merge que consentir ahí. El razonamiento sigue siendo correcto sobre el riesgo de
 * CONTAMINAR un cliente, y por eso el orden y el rótulo «sin confirmar» lo conservan; lo que no
 * contemplaba es el otro costo, el del volumen: sobre 246 propuestas, «no hay merge que consentir»
 * igual significaba doscientas líneas que nadie pidió.
 *
 * Lo que la medición contra producción sigue cerrando, y no hay que reabrir: pre-tildar por
 * `confidence === 'HIGH'`. La precisión es **0,69 macro / 0,79 micro** contra el 0,85 que este mismo
 * archivo fijaba como condición, y la distribución es **bimodal** —34 de 62 agencias en 0,80 o más y
 * el resto agrupa mal—, así que ningún umbral global separa los dos mundos. El toggle de agrupación
 * (`SET_GROUPING`) le entrega esa decisión al usuario, que sí conoce su cartera.
 *
 * ⚠️ Con esto, el «camino de mínimo esfuerzo» **ya no llega al final sin tocar nada**: la pantalla
 * abre con el botón deshabilitado, y el tilde maestro pasa a ser el control principal — no un atajo.
 */
export const PROPOSAL_MODE = {
    pretickMaxGroupSize: 0,
    // Sumar cuentas a un cliente que ya tiene historia también es un merge, y de los caros: si el
    // match está mal, contamina un cliente vivo con datos de otro negocio.
    pretickExistingAssignments: false,
    doubtConfidence: 'MEDIUM',
};

/**
 * Como el modo activo, pero además SIN preguntas.
 *
 * Desde que `PROPOSAL_MODE` no pre-tilda nada, lo único que lo distingue es `doubtConfidence`: acá
 * una cuenta dudosa entra al grupo sin preguntar nada y el usuario la corrige después, en vez de
 * responder la pregunta en la fila. Es la posición de repuesto si las dudas resultaran ser más
 * ruido que ayuda; hoy no se usa.
 */
export const CONSERVATIVE_PROPOSAL_MODE = {
    pretickMaxGroupSize: 0,
    pretickExistingAssignments: false,
    doubtConfidence: null,
};

/**
 * El comportamiento anterior: tildar todo lo que el agrupador propone.
 *
 * Se conserva para poder volver sin reescribir nada **el día que la precisión lo justifique** — que
 * hoy no es el caso. Requiere medir de nuevo con `spikes/spike_client_detection_mcc.py`.
 */
export const TRUSTING_PROPOSAL_MODE = {
    pretickMaxGroupSize: Infinity,
    pretickExistingAssignments: true,
    doubtConfidence: 'MEDIUM',
};

/**
 * Subirla descarta todos los borradores guardados. Se subió a 2 el 24/09 (review C-paso3-05): el paso
 * 3 dejó de nacer tildado, pero el plazo del borrador se renueva en cada guardado, así que quien
 * volvía todos los días seguía viendo los defaults viejos para siempre.
 */
export const STORAGE_VERSION = 2;

/**
 * Lo que acepta `AgencyClient._name` (`CharField(50)`); `add-bulk` rebota con `CLIENT_NAME_TOO_LONG`.
 * El input del rename ya corta en 50 (`ClientProposalRow`), que podría importarlo de acá para que el
 * número viva en un solo lugar.
 */
export const CLIENT_NAME_MAX_LENGTH = 50;

/**
 * Una cuenta sin actividad reciente NO viene tildada, aunque ya tenga cliente.
 *
 * El backend las separa (`activity`, y una lista `dormant` aparte para las que además no tienen
 * dueño) y hasta ahora el frontend ignoraba las dos cosas: una cuenta dormida ya vinculada entraba
 * tildada y salía con línea de control, y una dormida sin dueño caía al cajón de revisión con el
 * motivo genérico de «no supimos de quién es», que no es lo que le pasa.
 *
 * **La decisión es ofrecerlas, no incluirlas.** Una línea de control existe para vigilar un
 * presupuesto que se está gastando; sobre una cuenta que hace meses no gasta, lo único que produce
 * es una fila apagada en el tablero y una alerta de «sin datos». Esconderlas sería peor —la agencia
 * puede estar por reactivar la cuenta y es su decisión, no nuestra—, así que viajan, se ven, dicen
 * por qué están ahí y esperan un clic.
 */
export const ACTIVITY_DORMANT = 'DORMANT';
export const REASON_DORMANT = 'DORMANT';

// La PLATAFORMA dice que la cuenta ya no puede operar (cancelada en Google, archivada en DV360…).
// Es distinto de DORMANT, que es «no pasó nada últimamente» y se revierte solo: esto es terminal del
// otro lado, y una línea creada sobre una cuenta así no va a traer datos nunca.
export const LIVENESS_DEAD = 'DEAD';
export const REASON_PLATFORM_DISABLED = 'PLATFORM_DISABLED';

/**
 * Una cuenta suelta con un nombre que SÍ sirve de rótulo («Iga», «Oro Sur»): el agrupador sabe cómo
 * se llamaría el cliente, pero no tiene con qué juntarla ni evidencia de que lo sea. No es «no
 * supimos de quién es», y el cajón no puede decírselo así.
 */
export const REASON_UNMATCHED = 'UNMATCHED';

/**
 * Una cuenta que el USUARIO sacó de un cliente propuesto («Sacar de este cliente», o «No, es otro
 * cliente» sobre una duda). No es que el nombre no diga de quién es: el nombre se lee bien, lo que
 * pasó es que alguien decidió que no iba ahí.
 *
 * Hasta el 30/09 la cuenta bajaba al cajón sin motivo y caía en el texto de `OPAQUE` —«el nombre no
 * dice de quién es»— sobre «CL - Increase Q10», con el mismo cajón proponiéndole «Crear «Increase»»
 * (card UxlFOkTh, punto 1). La marca que el cajón lee es `unassignedFrom` (el cliente del que salió),
 * no este `reason`: descartarla y recuperarla borra el `reason` (ver `dismissChange`) y la cuenta
 * tiene que seguir diciendo de dónde salió.
 */
export const REASON_UNASSIGNED = 'UNASSIGNED';

/**
 * Una cuenta que la plataforma dice que NO invirtió en los últimos 7 días (C-02, recorrido con Pablo
 * del 13/10): «proponer sólo las cuentas con movimiento». El backend la manda en su propia lista
 * (`no_recent_spend`) y no la propone, aunque tenga una campaña ACTIVE; acá queda en el cajón,
 * buscable y asignable, en «Sin inversión esta semana». Es una decisión del backend: el FE no
 * reclasifica con `spent_last_7d`.
 */
export const REASON_NO_RECENT_SPEND = 'NO_RECENT_SPEND';

/** Cómo se están mostrando los grupos que propuso Dashbo. */
export const GROUPING_PROPOSED = 'proposed';
export const GROUPING_ONE_PER_ACCOUNT = 'one_per_account';

export function initialState() {
    return {
        decisions: {byAccount: {}, clientRenames: {}},
        grouping: GROUPING_PROPOSED,
        budgetEdits: {},
        // La conversión elegida por cuenta: `{[pk]: [actionId]}`, y `[]` = «la elijo después». Una
        // cuenta sin entrada usa la sugerencia (ver `conversionSuggestion.js`).
        conversionEdits: {},
        // `payloadSignature` es la cartera que se posteó. NO decide si se postea —eso lo decide el
        // gesto del usuario (`APPLY_REQUESTED`, ver `applyPlanFor`)—: sirve para adoptar el resultado
        // de un POST que terminó mientras la pantalla no estaba (`settledWhileAway`) y para el guard
        // del doble montaje de desarrollo.
        // `readiness` = `{ready, total}` clientes listos al salir de «Creando tu cartera», o `null` si
        // no se supo. Lo lee la pantalla final para no decir «Listo» con clientes a medio hacer.
        apply: {status: 'idle', payloadSignature: null, createdIds: [], createdClients: [], skipped: [],
                rowErrors: {}, requestErrors: [], genericError: null, readiness: null},
    };
}

export function wizardReducer(state, action) {
    switch (action.type) {
        case 'RECONCILE':
            return reconcile(state, action);
        case 'TOGGLE_ACCOUNT':
            return withAccount(state, action.pk,
                               decision => ({...decision, include: !decision.include, unconfirmed: false}));
        case 'SET_INCLUDE_MANY':
            return withAccounts(state, action.pks,
                                decision => ({...decision, include: Boolean(action.include),
                                              unconfirmed: unconfirmedAfterMany(decision, action)}));
        case 'SET_DISMISSED_MANY':
            return withAccounts(state, action.pks, decision => dismissChange(decision, action.dismissed, action.at));
        case 'SPLIT_ACCOUNT':
            return splitAccount(state, action.pk, action.fallbackName);
        case 'UNASSIGN_ACCOUNT': {
            // Sacarla del cliente NO es descartarla: vuelve al cajón esperando que alguien diga de
            // quién es, que es de donde el usuario la puede recuperar. Y lleva su motivo: la sacó
            // una persona, de un cliente con nombre (ver `takenOutOf`).
            const out = takenOutOf(state.decisions.byAccount[action.pk], state.decisions.clientRenames);
            return withAccount(state, action.pk, decision => ({...decision, clientRef: null, include: false,
                                                               needsAssignment: true, splitName: null,
                                                               doubtAnswer: undefined, isDoubt: false,
                                                               ...out}));
        }
        case 'ASSIGN_ACCOUNT':
            // Asignar un cliente vuelve a incluir la cuenta: si el usuario se tomó el trabajo de
            // decirnos de quién es, dejarla afuera sería descartar justamente lo que acaba de hacer.
            //
            // Y se le borra el `reason` por lo mismo que en `dismissChange`: ya no está en el cajón
            // por lo que el sistema dijo de ella. Sin esto, una dormida a la que el usuario le
            // asignaba cliente y después sacaba volvía a la sección de dormidas afirmando «ya es de
            // <cliente>» sobre un cliente que todavía no existe.
            return withAccount(state, action.pk, decision => {
                const {reason, platformReason, unassignedFrom, ...resto} = decision;
                return {...resto, clientRef: action.clientRef, include: true, dismissed: false};
            });
        case 'ANSWER_DOUBT': {
            // "No es de este cliente" no adivina de quién es: la cuenta queda sin asignar, que es un
            // estado honesto y accionable, en vez de mandarla a otro grupo por descarte. Y baja al
            // cajón con el mismo motivo que «Sacar de este cliente»: es el mismo gesto.
            const out = action.belongs
                ? {}
                : takenOutOf(state.decisions.byAccount[action.pk], state.decisions.clientRenames);
            return withAccount(state, action.pk, decision => ({
                ...decision,
                doubtAnswer: action.belongs,
                clientRef: action.belongs ? action.clientRef : null,
                include: action.belongs,
                ...out,
            }));
        }
        case 'SET_GROUPING':
            return setGrouping(state, action.grouping);
        case 'DISMISS_ACCOUNT':
            return withAccount(state, action.pk, decision => dismissChange(decision, true, action.at));
        case 'RESTORE_ACCOUNT':
            return withAccount(state, action.pk, decision => dismissChange(decision, false));
        case 'RENAME_CLIENT':
            return {...state, decisions: {...state.decisions,
                                          clientRenames: {...state.decisions.clientRenames,
                                                          [action.originalName]: action.newName}}};
        case 'EDIT_BUDGET':
            return {...state, budgetEdits: {...state.budgetEdits,
                                            [action.pk]: {amount: action.amount, currency: action.currency}}};
        case 'CLEAR_BUDGET_EDIT': {
            // Vuelve al monto propuesto: «Como el mes pasado» en el aviso de ritmo (UD-22). Sacar la
            // edición y no escribir el monto propuesto encima, para que la cuenta siga leyéndose como
            // «no la tocó» (el aviso y el resumen de arriba dependen de eso).
            const {[action.pk]: _dropped, ...budgetEdits} = state.budgetEdits;
            return {...state, budgetEdits};
        }
        case 'EDIT_CONVERSION':
            return {...state, conversionEdits: {...state.conversionEdits, [action.pk]: action.actionIds}};
        case 'CONFIRM_CONVERSIONS':
            // Al salir del paso 4 la sugerencia pasa a ser la elección: vive en el servidor sólo
            // mientras dura la pestaña, y una recarga en «Creando tu cartera» la perdería. Lo que el
            // usuario ya tocó no se pisa.
            return {...state, conversionEdits: {...action.conversions, ...state.conversionEdits}};
        case 'APPLY_REQUESTED':
            // El GESTO del usuario: «Crear…» en presupuestos o «Crear el resto» en el cartel de
            // error. Es la única puerta al POST (ver `applyPlanFor`): un cambio de estado cualquiera
            // —adoptar los clientes que el alta fallida dejó creados, por ejemplo— ya no alcanza.
            return {...state, apply: {...state.apply, status: 'requested'}};
        case 'APPLY_STARTED':
            return {...state, apply: {...initialState().apply, status: 'running',
                                      payloadSignature: action.payloadSignature || null}};
        case 'APPLY_SUCCEEDED':
            return {...state, apply: {...state.apply, status: 'syncing', createdIds: action.createdIds,
                                      createdClients: action.createdClients, skipped: action.skipped}};
        case 'APPLY_FAILED':
            return adoptCreatedClients({
                ...state,
                apply: {...state.apply, status: action.isPartial ? 'error_partial' : 'error_items',
                        rowErrors: action.rowErrors, requestErrors: action.requestErrors || [],
                        createdClients: action.createdClients, genericError: action.genericError || null},
            }, action.createdClients);
        case 'SYNC_FINISHED':
            return {...state, apply: {...state.apply, status: 'done', readiness: action.readiness || null}};
        case 'HYDRATE':
            return {...state, decisions: action.decisions || state.decisions,
                    budgetEdits: action.budgetEdits || state.budgetEdits,
                    conversionEdits: action.conversionEdits || state.conversionEdits};
        default:
            return state;
    }
}

/**
 * Los clientes que el alta alcanzó a crear antes de fallar pasan a ser clientes EXISTENTES.
 *
 * El backend no los revierte a propósito —así el reintento los reusa en vez de duplicarlos— pero si
 * la pantalla los sigue tratando como "clientes a crear", el segundo intento vuelve a mandar el
 * mismo nombre y rebota con «ya existe un cliente llamado así» sobre una fila que el usuario ve
 * perfecta. Sin salida, salvo recargar y asignarlos a mano.
 *
 * Se compara por nombre porque es lo único que las dos puntas comparten: el backend devuelve el
 * nombre con el que los creó, que es exactamente el que mandó la pantalla.
 */
function adoptCreatedClients(state, createdClients) {
    if (!createdClients || !createdClients.length) return state;

    const idByName = {};
    createdClients.forEach(client => {
        if (client?.name) idByName[client.name] = client.id;
    });

    const byAccount = {};
    Object.keys(state.decisions.byAccount).forEach(pk => {
        const decision = state.decisions.byAccount[pk];
        const name = decision.clientRef?.type === 'new'
            ? (state.decisions.clientRenames[decision.clientRef.name] || decision.clientRef.name)
            : null;
        const id = name ? idByName[name] : undefined;
        byAccount[pk] = id === undefined
            ? decision
            : {...decision, clientRef: {type: 'existing', id}, proposedName: name};
    });

    return {...state, decisions: {...state.decisions, byAccount}};
}

function withAccount(state, pk, change) {
    return withAccounts(state, [pk], change);
}

/**
 * El mismo cambio sobre varias cuentas, en UNA sola reducción.
 *
 * No es una comodidad: es la única forma en que una acción masiva puede existir en este paso. La
 * alternativa —despachar una acción por cuenta, como hacía el tilde del grupo— multiplica por N las
 * reducciones y los renders, y sobre las 638 filas que puede tener una cartera grande eso son miles
 * de pasadas para un solo gesto del usuario. Todo lo que actúa sobre más de una cuenta (tildar la
 * lista, eliminar un cliente propuesto, el tilde de la fila) entra por acá.
 */
/**
 * Qué pasa con «sin confirmar» después de una acción MASIVA.
 *
 * Tres casos, y sólo uno es obvio:
 *
 * 1. **La acción lo pide explícitamente** — es el deshacer del masivo, que restituye la fila entera
 *    y no sólo su tilde. Manda sobre todo lo demás.
 * 2. **Tildar es confirmar**: el usuario acaba de decir que sí. De ahí en más el estado es suyo.
 * 3. **Destildar lo que YA estaba apagado no es un gesto sobre esa fila**, así que conserva su
 *    estado. Esto es lo que antes se perdía: `Boolean(action.unconfirmed)` con la clave ausente da
 *    `false`, así que un solo clic en «Quitar la selección» borraba el «sin confirmar» de TODAS las
 *    filas de la lente —incluidas las que el usuario nunca había tocado— y el estado quedaba escrito
 *    en el borrador, sin forma de recuperarlo salvo deshaciendo en el acto. Con la cartera entera
 *    naciendo destildada, ese clic es el primero que mucha gente va a dar.
 */
function unconfirmedAfterMany(decision, action) {
    if (action.unconfirmed !== undefined) return Boolean(action.unconfirmed);
    if (action.include) return false;
    return Boolean(decision.unconfirmed) && !decision.include;
}

function withAccounts(state, pks, change) {
    const byAccount = {...state.decisions.byAccount};
    (pks || []).forEach(pk => {
        byAccount[pk] = change(byAccount[pk] || {include: false, clientRef: null});
    });
    return {...state, decisions: {...state.decisions, byAccount}};
}

/**
 * Descartar y volver a traer.
 *
 * Deshacer devuelve **exactamente** lo que había: se recuerda si la cuenta estaba tildada al
 * descartarla y se restituye eso. Sin esa memoria, restaurar tenía que adivinar, y adivinar «sí» es
 * la peor de las dos opciones: un grupo de varias cuentas volvería TILDADO, o sea con su merge
 * aprobado, cuando el usuario sólo apretó «Deshacer» sobre algo que había sacado.
 */
function dismissChange(decision, dismissed, at) {
    if (dismissed) {
        // `dismissedAt` guarda DESDE QUÉ PASO lo sacó una persona, y viaja en el borrador como
        // cualquier otra decisión. No alcanza con mirar `includeBeforeDismiss` para saberlo: el
        // sistema también descarta —las cuentas muertas de la plataforma llegan con `dismissed:
        // true` y su `includeBeforeDismiss`—, así que sin esta marca el cajón de deshacer de un paso
        // le ofrecería al usuario «recuperar» cuentas que él nunca sacó.
        //
        // ⚠️ Y el `reason` se BORRA, porque deja de ser cierto. `reason` dice por qué la apartó el
        // SISTEMA (dormida, cerrada); en cuanto la saca una persona, el motivo es su gesto. Sin
        // esto, descartar una dormida no hacía nada visible: volvía a dibujarse en «Sin actividad
        // hace meses» con sus mismos tres botones, en vez de bajar a «las que sacaste». Sólo pasa
        // por acá el gesto del usuario — el sistema arma la decisión descartada directamente, en
        // `withPlatformLiveness` y en `decisionFromAccount`.
        const {reason, platformReason, ...resto} = decision;
        return {...resto, dismissed: true, includeBeforeDismiss: Boolean(decision.include),
                include: false, dismissedAt: at || null};
    }
    // Restaurar lo que NO está descartado no toca nada. Pasaba con el «Deshacer» del aviso del tacho
    // después de haber recuperado la cuenta desde el cajón y haberla tildado: pisaba ese tilde con
    // el `include` de antes de eliminarla (review C-paso3-06).
    if (!decision.dismissed) return decision;
    const {includeBeforeDismiss, dismissedAt, ...rest} = decision;
    const include = includeBeforeDismiss === undefined ? false : includeBeforeDismiss;
    // Si vuelve APAGADA, vuelve además «sin confirmar»: nació así, no la apagó nadie. Sin esto, la
    // cuenta que sale del cajón con «Usar igual» ofrecía «Deshacer» —revertir algo que el usuario
    // no hizo—, que es exactamente el defecto que este frente vino a sacar del resto de la pantalla.
    return {...rest, dismissed: false, include, unconfirmed: !include};
}

/**
 * Una cuenta sale de su grupo y pasa a ser su propio cliente.
 *
 * Es el gesto fino que el toggle de agrupación no puede dar: aquél separa el padrón entero, y acá se
 * corrige UN merge sin tocar los otros doscientos. Reusa el mismo desambiguado de nombres
 * (`availableName`) porque el destino se identifica por el nombre en las dos puntas — separarla
 * dejándole un nombre ya ocupado la devolvería al grupo del que se la quiso sacar.
 *
 * **Queda del MISMO LADO que el cliente del que sale**: tildada si ese cliente estaba elegido, y sin
 * elegir si no. Hasta el 30/09 nacía tildada siempre —«una cuenta sola no es un merge»—, y como el
 * paso 3 parte la lista en «listos» y «pendientes», separar una cuenta de un cliente sin elegir
 * mandaba a los dos a secciones distintas: la cuenta nueva arriba de todo y el original saltando
 * de lugar (card 03NF0The). Y deja de ser una duda: la pregunta era «¿es de este cliente?», y acaba
 * de dejar de estarlo.
 */
function splitAccount(state, pk, fallbackName) {
    const decision = state.decisions.byAccount[pk];
    if (!decision) return state;
    // «Elegido» es la misma pregunta que contesta la fila (`groupIsIncluded`): alguna de sus cuentas
    // —dudas incluidas— está tildada.
    const groupChosen = Object.values(state.decisions.byAccount).some(other =>
        !other.dismissed && other.include && sameClientRef(other.clientRef, decision.clientRef));

    // ⚠️ **El nombre del grupo del que sale no puede ser el nombre con el que sale.** `soloName` es
    // el nombre que calculó el backend con la denylist de la agencia, pero cuando la cuenta no traía
    // uno propio, `proposalDecisions` lo completa con el nombre del GRUPO. Para el toggle global eso
    // da igual —salen todas juntas y el desempate numera— pero acá el grupo se queda donde está:
    // sacar «Nookit LLC» de «Acciones» producía un cliente nuevo llamado «Acciones», que es lo
    // contrario de lo que el usuario pidió. Verificado en pantalla con la propuesta real.
    const groupName = decision.clientRef?.type === 'new'
        ? (state.decisions.clientRenames[decision.clientRef.name] || decision.clientRef.name)
        : decision.proposedName;
    const solo = decision.soloName && decision.soloName !== groupName ? decision.soloName : null;
    const wanted = solo || fallbackName || decision.soloName || String(pk);
    const name = availableName(wanted, takenNamesOf(state.decisions.byAccount, new Set([String(pk)])));

    return withAccount(state, pk, current => ({...current, clientRef: {type: 'new', name}, splitName: name,
                                               include: groupChosen, unconfirmed: !groupChosen,
                                               isDoubt: false, doubtAnswer: undefined,
                                               dismissed: false}));
}

/**
 * Lo que una cuenta lleva al cajón cuando el USUARIO la saca de su cliente.
 *
 * El motivo y de dónde salió (`REASON_UNASSIGNED`), y el nombre que el cajón le propone para crearle
 * un cliente propio. Ese nombre NO puede ser el del cliente del que se sacó: conservaba el
 * `proposedName` del grupo, así que «Crear «Zeta Grupo»» la devolvía al mismo grupo, y sobre un
 * cliente que ya existe («Acme») armaba un cliente nuevo con su nombre, que `add-bulk` rechaza con
 * `CLIENT_NAME_TAKEN` (review C-paso3-03). Es el mismo criterio que `splitAccount`: el nombre suelto
 * de la cuenta si es otro, y si no hay, ninguno (el cajón ofrece «Crear cliente» con el nombre de
 * la cuenta).
 */
function takenOutOf(decision, clientRenames) {
    const from = clientNameOf(decision, clientRenames);
    const own = decision?.soloName;
    const proposedName = own && own !== from && own !== decision?.clientRef?.name ? own : null;
    return {reason: REASON_UNASSIGNED, unassignedFrom: from, proposedName};
}

/** El nombre con el que la pantalla muestra el cliente de una decisión (el renombre gana). */
function clientNameOf(decision, clientRenames) {
    if (!decision?.clientRef) return null;
    if (decision.clientRef.type === 'existing') return decision.proposedName || null;
    return (clientRenames || {})[decision.clientRef.name] || decision.clientRef.name;
}

/**
 * Los nombres de cliente nuevo que ya están ocupados, salvo los de las cuentas que se están moviendo.
 *
 * Excluir las que se mueven es lo que hace que separar dos veces seguidas dé lo mismo: sin eso, la
 * cuenta chocaría contra su propio nombre y se iría sumando un sufijo en cada pasada.
 */
export function takenNamesOf(byAccount, movingPks = new Set()) {
    const taken = new Set();
    Object.keys(byAccount || {}).forEach(pk => {
        const decision = byAccount[pk];
        const clientRef = decision.clientRef;
        // Un cliente que YA EXISTE ocupa su nombre igual que uno por crear: `add-bulk` rebota con
        // `CLIENT_NAME_TAKEN` si el `new_client_name` es el de un cliente vivo de la agencia. Sin
        // esto, separar una cuenta de un grupo asignado a un cliente existente producía un cliente
        // nuevo con SU nombre — un 422 sobre una tarjeta que el usuario ve perfecta.
        //
        // ⚠️ Y se cuenta **aunque la cuenta que lo trae sea la que se está moviendo**: ese nombre no
        // está ocupado por la propuesta sino por la base. Excluirla es correcto sólo para los
        // clientes por crear, donde el destino se identifica por el nombre y una cuenta no puede
        // chocar consigo misma.
        if (clientRef?.type === 'existing' && decision.proposedName) {
            taken.add(decision.proposedName);
            return;
        }
        if (movingPks.has(String(pk))) return;
        if (clientRef?.type === 'new') taken.add(clientRef.name);
    });
    return taken;
}

/**
 * Una decisión guardada, completada con los campos que el esquema NO tenía cuando se guardó.
 *
 * El borrador del paso 3 vive en `localStorage` y gana sobre la propuesta: al volver, la pantalla
 * sirve lo guardado. Eso es correcto —lo que el usuario decidió no se pisa— pero tiene un costo que
 * apareció en el QA del 08/09: **una agencia que ya abrió el paso 3 no ve NINGÚN arreglo posterior**.
 * Sus decisiones no tienen `unconfirmed`, ni el cliente propio de las cuentas cerradas, así que la
 * pantalla se comporta como antes de arreglarla. Y son justo las agencias con las que se probó.
 *
 * Se MIGRA en vez de descartar el borrador: descartarlo también se llevaría los presupuestos
 * editados a mano, que es trabajo real y del que no hay copia.
 *
 * La regla es una sola: **si la decisión guardada coincide con la que la propuesta daría hoy, el
 * usuario no tocó esa fila** y se puede recalcular entera. Si difiere en algo que él controla
 * —el tilde, el cliente, haberla sacado— es suya y no se toca.
 *
 * ⚠️ Caso de borde aceptado: un merge que nació destildado y que el usuario destildó "de nuevo"
 * es indistinguible de uno que nunca tocó, porque el dato que los separaba es justamente el que
 * falta. Esa fila puede volver diciendo «sin confirmar». Es el mismo destino (no se crea) y el
 * costo es un rótulo, contra perder los presupuestos de toda la cartera.
 */
function migratedDecision(previous, fresca) {
    // Borrador ya guardado con el esquema nuevo: no hay nada que migrar.
    if (previous.unconfirmed !== undefined) return previous;
    if (previous.include !== fresca.include) return previous;
    if (Boolean(previous.dismissed) !== Boolean(fresca.dismissed)) return previous;
    if (previous.doubtAnswer !== undefined) return previous;
    if (!sameClientRef(previous.clientRef, fresca.clientRef)) return previous;
    return fresca;
}

/** Dos referencias a cliente son la misma si apuntan al mismo lugar. */
function sameClientRef(one, other) {
    if (!one || !other) return !one && !other;
    if (one.type !== other.type) return false;
    return one.type === 'existing' ? one.id === other.id : one.name === other.name;
}

/**
 * Las decisiones puestas al día contra lo que el servidor dice ahora.
 *
 * Descarta las cuentas que ya no están o que quedaron cubiertas por una línea (alguien más las
 * configuró mientras tanto) y suma las nuevas con el default que corresponda al modo. Lo que el
 * usuario ya tocó no se pisa: su decisión gana sobre la propuesta.
 */
export function reconcile(state, {candidates, proposals, refreshFrom = null, mode = PROPOSAL_MODE}) {
    const offerable = {};
    (candidates?.accounts || []).forEach(account => {
        if (account.covered) return;
        offerable[String(account.id)] = account;
    });

    const proposed = proposalDecisions(proposals, mode);
    // La propuesta ANTERIOR, cuando la nueva es el refresco de la misma pantalla (C-02: el gasto de
    // 7 días llega mientras el usuario ya está mirando el paso 3). Ver abajo.
    const before = refreshFrom ? proposalDecisions(refreshFrom, mode) : null;
    const byAccount = {};

    Object.keys(offerable).forEach(pk => {
        const previous = state.decisions.byAccount[pk];
        const fresca = withPlatformLiveness(offerable[pk],
                                            proposed[pk] || decisionFromAccount(offerable[pk]));
        // En un refresco, la fila que el usuario NO tocó sigue a la propuesta nueva: si su decisión
        // es exactamente la que daba la propuesta anterior, nadie la cambió. Es lo que hace que una
        // cuenta que resultó no invertir en la semana baje al cajón aunque la pantalla ya la
        // estuviera mostrando propuesta. Lo que el usuario tocó no se mueve, y renombrar el cliente
        // también es tocarlo: el renombre vive aparte (`clientRenames`), no en la decisión.
        if (previous && before && !isRenamed(previous, state.decisions.clientRenames)) {
            const was = withPlatformLiveness(offerable[pk], before[pk] || decisionFromAccount(offerable[pk]));
            if (sameDecision(previous, was)) {
                byAccount[pk] = fresca;
                return;
            }
        }
        // Sin la propuesta anterior —volver al paso, recargar con el borrador guardado— no hay contra
        // qué comparar, pero una cuenta que el backend ya no propone porque no invirtió en la semana
        // (C-02) baja igual si su fila sigue como nació. Sin esto, el gasto que llegó con el usuario
        // fuera del paso no se aplicaba nunca (review C-rp-fe-alta-01 de la ronda 2).
        if (previous && fresca.reason === REASON_NO_RECENT_SPEND
                && isUntouchedProposal(previous, state.decisions.clientRenames)) {
            byAccount[pk] = fresca;
            return;
        }
        // Ya decidió sobre esta cuenta: se respeta tal cual, aunque la propuesta haya cambiado.
        // Eso incluye haberla RECUPERADO del cajón: una cuenta que la plataforma reporta muerta y que
        // el usuario sacó de ahí no vuelve a caer sola en el próximo reconcile.
        byAccount[pk] = previous ? migratedDecision(previous, fresca) : fresca;
    });

    return {...state, decisions: {...state.decisions, byAccount}};
}

/**
 * ¿Esta fila sigue tal como la propuso el agrupador, sin que nadie la toque?
 *
 * Es la forma con la que nace una cuenta propuesta (`proposalDecisions`): destildada, «sin
 * confirmar», en el cliente nuevo que propuso el agrupador y sin motivo de cajón. Cualquier gesto la
 * cambia: tildar (apaga `unconfirmed`), sacarla (`dismissed`), separarla (`splitName`), sacarla del
 * cliente (`clientRef`), contestar la duda (`doubtAnswer`) o renombrar su cliente (`clientRenames`).
 */
export function isUntouchedProposal(decision, clientRenames) {
    if (!decision || decision.include || decision.dismissed || decision.unconfirmed !== true) return false;
    if (decision.doubtAnswer !== undefined || decision.splitName || decision.reason) return false;
    const ref = decision.clientRef;
    if (ref?.type !== 'new' || ref.name !== decision.proposedName) return false;
    return !isRenamed(decision, clientRenames);
}

function isRenamed(decision, clientRenames) {
    const ref = decision?.clientRef;
    return ref?.type === 'new' && Boolean((clientRenames || {})[ref.name]);
}

/**
 * Dos decisiones iguales campo por campo, también adentro (`clientRef`, `evidence`). Un campo
 * `undefined` cuenta como ausente, y el orden de las claves no importa.
 */
function sameDecision(one, other) {
    return JSON.stringify(canonicalOf(one)) === JSON.stringify(canonicalOf(other));
}

function canonicalOf(value) {
    if (Array.isArray(value)) return value.map(canonicalOf);
    if (!value || typeof value !== 'object') return value;
    const out = {};
    Object.keys(value).sort().forEach(key => {
        if (value[key] !== undefined) out[key] = canonicalOf(value[key]);
    });
    return out;
}

/**
 * La decisión de una cuenta sobre la que el agrupador no dijo nada.
 *
 * Casi siempre es una cuenta sin dueño, y queda esperando que alguien diga de quién es. Pero hay un
 * caso que NO es ese: la cuenta que ya está vinculada a un cliente y a la que sólo le falta la
 * línea. Esas no aparecen en la propuesta —el agrupador sólo trabaja sobre lo que no tiene dueño—,
 * así que sin mirar acá terminarían en el cajón de «revisá estas» pidiéndole al usuario que asigne
 * un cliente que ya está asignado.
 */
/**
 * Una cuenta que la plataforma reporta MUERTA nace fuera de la propuesta.
 *
 * No es un filtro de presentación: entra al mismo cajón de «sacadas» que usa el gesto manual, con el
 * mismo deshacer. Esa es la razón de reusar `dismissed` en vez de inventar un estado nuevo — el
 * camino de recuperación ya existe, está probado y el usuario ya lo conoce.
 *
 * Se conserva `include` en false y se anota el motivo para que el cajón pueda decir POR QUÉ está ahí:
 * «la sacaste vos» y «Google la reporta cancelada» piden copys distintos.
 *
 * ⚠️ Sólo corre sobre decisiones NUEVAS (ver `reconcile`). Si pisara las existentes, recuperar una
 * cuenta del cajón duraría hasta el siguiente refresco de la propuesta.
 */
function withPlatformLiveness(account, decision) {
    if (account?.liveness !== LIVENESS_DEAD) return decision;
    return {...decision,
            include: false,
            // Lo que «Usar igual» tiene que devolver. Sin esto, RESTORE_ACCOUNT cae en el default de
            // `dismissChange` (`includeBeforeDismiss === undefined → false`) y la cuenta sale del
            // cajón DESTILDADA: el usuario aprieta el botón, la fila aparece, y «Crear N líneas» no
            // se mueve — exactamente la sensación de botón roto que este frente vino a sacar del
            // otro lado. Duele sobre todo en la cuenta que YA está vinculada a un cliente, que nacía
            // tildada y era la única a la que no le falta nada más para crearse.
            // `?? Boolean(decision.include)` y no a secas: una cuenta que llega en la lista `dead`
            // del backend nace con `include:false` —nunca estuvo en la propuesta, porque se aparta
            // ANTES de agrupar— así que sin esto el deshacer la devolvía SIEMPRE destildada. Esas
            // decisiones declaran en `proposalDecisions` con qué estado tienen que volver.
            //
            // Se lee de `includeBeforeDismiss` y NO se pone `include: true` a propósito: dejar
            // `include` en false hasta que el usuario apriete «Usar igual» es lo que impide que una
            // cuenta muerta entre sola a la cartera si el payload de candidatas y el de la propuesta
            // llegaran desfasados.
            includeBeforeDismiss: decision.includeBeforeDismiss ?? Boolean(decision.include),
            dismissed: true,
            reason: REASON_PLATFORM_DISABLED,
            platformReason: account.liveness_reason || null};
}

function decisionFromAccount(account) {
    const isDormant = account?.activity === ACTIVITY_DORMANT;
    if (account?.agency_client) {
        // La dormida CON dueño baja al cajón, igual que la que no lo tiene.
        //
        // Hasta acá se quedaba en la lista, apagada y con su pastilla: era la decisión del 02/09
        // («se ofrecen, no se incluyen»), tomada cuando la lista era lo único que había. Con el
        // cajón ya construido y una cartera de cientos, esa fila sólo agrega ruido arriba —
        // la primera pantalla tiene que mostrar lo que sirve. fede, 2026-09-22.
        //
        // Conserva su `clientRef`: el vínculo con el cliente ya existe en la base y perderlo
        // obligaría a reasignarla a mano para recuperarla. `includeBeforeDismiss` queda en false
        // para que «Usar igual» la devuelva destildada, como todo lo demás.
        if (isDormant) {
            return {
                include: false,
                includeBeforeDismiss: false,
                clientRef: {type: 'existing', id: account.agency_client.id},
                proposedName: account.agency_client.name,
                confidence: 'HIGH',
                isDoubt: false,
                isDormant: true,
                dismissed: true,
                reason: REASON_DORMANT,
            };
        }
        return {
            // Nace destildada como todo lo demás (ver `PROPOSAL_MODE`). Ésta era la fuente REAL del
            // «viene todo preseleccionado»: no la propuesta del agrupador —que ya pre-tildaba poco—
            // sino la cuenta que en la base YA cuelga de un cliente, que es la mayoría del padrón en
            // una agencia con su cartera armada. Medirlo en una agencia nueva no lo muestra: ahí casi
            // ninguna cuenta tiene `agency_client` y la pantalla se ve casi entera destildada.
            include: false,
            clientRef: {type: 'existing', id: account.agency_client.id},
            proposedName: account.agency_client.name,
            confidence: 'HIGH',
            isDoubt: false,
            // Nació apagada, no la apagó nadie — ver `proposalDecisions`. Sin esto, la familia más
            // grande del padrón de una agencia armada ofrecería «Deshacer» de entrada.
            unconfirmed: true,
            isDormant,
            dismissed: false,
        };
    }
    return {include: false, clientRef: null, needsAssignment: true, isDormant,
            reason: isDormant ? REASON_DORMANT : undefined, dismissed: false};
}

/**
 * Lo que el agrupador propone, traducido a decisiones iniciales.
 *
 * **Nada viene tildado** (ver `PROPOSAL_MODE`): todo nace destildado y `unconfirmed`, los dudosos
 * además llevan la pregunta a la vista, y lo que no se pudo asignar queda sin cliente. Lo que
 * distingue a un grupo de una cuenta de un merge ya no es el tilde sino el rótulo «sin confirmar»,
 * que marca dónde hay una decisión de agrupación sin mirar.
 */
export function proposalDecisions(proposals, mode = PROPOSAL_MODE) {
    const decisions = {};

    (proposals?.proposed_clients || []).forEach(group => {
        const accounts = group.accounts || [];
        const isTrusted = accounts.length <= (mode.pretickMaxGroupSize || 0);
        // La duda que se pregunta en la fila es «¿esta cuenta es de este cliente?», y sobre un grupo
        // de UNA cuenta no significa nada: el cliente propuesto *es* esa cuenta. Preguntarlo ahí
        // gastaría la atención del usuario justo donde no hay nada en juego.
        const isDoubt = Boolean(mode.doubtConfidence)
            && group.confidence === mode.doubtConfidence
            && accounts.length >= 2;
        accounts.forEach(account => {
            decisions[String(account.key)] = {
                include: Boolean(isTrusted),
                clientRef: {type: 'new', name: group.name},
                proposedName: group.name,
                // Con qué nombre nacería esta cuenta si el usuario decide separarlas. Lo calcula el
                // backend con la denylist dinámica de la agencia (su nombre, el del MCC, los tokens
                // frecuentes), que no viaja: acá no se puede recalcular.
                soloName: account.suggested_client_name || group.name,
                groupSize: accounts.length,
                confidence: group.confidence,
                evidence: account.evidence || null,
                isDoubt: Boolean(isDoubt),
                // Nace destildada porque NADIE la confirmó, no porque alguien la haya apagado.
                //
                // La distinción sobrevive al cambio de default y ahora pesa más: con
                // `pretickMaxGroupSize: 0`, `isTrusted` es siempre falso y esto vale para la
                // propuesta entera. Es lo que hace que la fila ofrezca «Incluir» y no «Deshacer»
                // —revertir algo que el usuario no hizo— en toda la pantalla, que es el defecto
                // que se arregló para los merges en 09/07 y que el cambio de default habría
                // reintroducido en las demás filas.
                //
                // Se apaga en cuanto el usuario toca la fila: a partir de ahí el estado SÍ es suyo.
                unconfirmed: !isTrusted,
                dismissed: false,
            };
        });
    });

    (proposals?.proposed_existing_assignments || []).forEach(assignment => {
        (assignment.accounts || []).forEach(account => {
            decisions[String(account.key)] = {
                include: Boolean(mode.pretickExistingAssignments),
                clientRef: {type: 'existing', id: assignment.agency_client.id},
                proposedName: assignment.agency_client.name,
                confidence: 'HIGH',
                evidence: account.evidence || null,
                isDoubt: false,
                // Igual que arriba, y por el mismo motivo: si nace apagada sin decir que nació
                // así, la fila ofrece deshacer algo que nadie hizo. Antes no hacía falta marcarlo
                // —era la única familia que podía nacer apagada estando el resto tildado— y el
                // cambio de default la puso en la misma situación que a las demás.
                unconfirmed: !mode.pretickExistingAssignments,
                dismissed: false,
            };
        });
    });

    (proposals?.unassigned || []).forEach(account => {
        decisions[String(account.key)] = {
            include: false,
            clientRef: null,
            proposedName: account.suggested_client_name,
            reason: account.reason,
            needsAssignment: true,
            dismissed: false,
        };
    });

    // Las dormidas viajan en su propia lista: no pasaron por el agrupador, así que no tienen ni
    // grupo ni motivo de descarte. Sin esto caían al mismo cajón que las opacas y se les explicaba
    // al usuario que no supimos de quién eran, cuando el problema es otro y lo sabemos.
    (proposals?.dormant || []).forEach(account => {
        decisions[String(account.key)] = {
            include: false,
            clientRef: null,
            proposedName: account.suggested_client_name,
            reason: REASON_DORMANT,
            needsAssignment: true,
            isDormant: true,
            dismissed: false,
        };
    });

    // Las que no invirtieron en los últimos 7 días (C-02): el backend tampoco las propone. Como las
    // dormidas, quedan sin cliente y esperando que alguien diga de quién son si hace falta.
    (proposals?.no_recent_spend || []).forEach(account => {
        decisions[String(account.key)] = {
            include: false,
            clientRef: null,
            proposedName: account.suggested_client_name,
            reason: REASON_NO_RECENT_SPEND,
            needsAssignment: true,
            dismissed: false,
        };
    });

    // Las muertas tampoco pasaron por el agrupador (el backend las aparta para que no contaminen la
    // propuesta de las vivas), así que llegan igual que las dormidas: sin grupo y sin cliente. Acá
    // nacen sin descartar a propósito — el que las manda al cajón es `withPlatformLiveness`, un solo
    // lugar, el mismo que corre para las que sí venían en la propuesta. Duplicar el descarte acá
    // haría que el estado del cajón dependa de por qué lista entró la cuenta.
    (proposals?.dead || []).forEach(account => {
        decisions[String(account.key)] = {
            // Nace destildada y al cajón la manda `withPlatformLiveness`, como a toda muerta. Lo que
            // se declara acá es CON QUÉ ESTADO tiene que volver si el usuario aprieta «Usar igual»:
            // tildada y como su propio cliente.
            //
            // Es la única salida del cajón de cerradas, y sin esto no llevaba a ninguna parte: la
            // cuenta salía de «La plataforma las reporta cerradas» y caía en «No supimos de quién
            // son» —con el motivo «la plataforma la reporta cerrada» contradiciendo el título del
            // grupo— sin mover el contador. El botón la cambiaba de cajón. Medido en el QA del
            // 08/09: 6 de 6 terminaban ahí.
            //
            // Cliente propio y no «asigname uno»: la muerta no pasó por el agrupador, así que no hay
            // grupo al que sumarla; y sabemos de quién es, porque el backend manda el nombre
            // propuesto. Pedirle al usuario que lo resuelva a mano sería fingir que no.
            include: false,
            includeBeforeDismiss: true,
            clientRef: {type: 'new', name: account.suggested_client_name},
            proposedName: account.suggested_client_name,
            soloName: account.suggested_client_name,
            groupSize: 1,
            confidence: 'HIGH',
            isDoubt: false,
            dismissed: false,
        };
    });

    return decisions;
}

/**
 * «Agrupadas como propone Dashbo» ⇄ «Cada cuenta por separado».
 *
 * Es la única pregunta del recorrido que el usuario contesta con certeza y el algoritmo nunca. La
 * inferencia es bimodal —funciona muy bien en poco más de la mitad de las agencias y mal en el
 * resto— y no hay señal en los datos que diga en cuál está una: la relación ~1:1 entre cuentas y
 * clientes, que es la que rompe el agrupamiento, sólo la conoce la agencia. Un clic le entrega esa
 * decisión, y de paso mide la hipótesis en campo.
 *
 * Es **reversible y no destructivo**: cada cuenta conserva su nombre de grupo (`proposedName`) y su
 * nombre suelto (`soloName`), así que volver no pierde nada. Sólo se mueven las cuentas que siguen
 * donde las puso Dashbo — si el usuario ya la mandó a otro cliente, esa decisión manda. Y al
 * reagrupar las cuentas quedan DESTILDADAS: vuelven a ser un merge, así que vuelven a pedir el
 * gesto.
 *
 * ⚠️ **El destino se identifica por el NOMBRE** (`clientGroupsOf` agrupa por `new:${name}`, y el
 * backend crea un cliente por `new_client_name`). Dos cuentas del mismo grupo con el mismo nombre
 * sugerido —la de Google y la de Meta del mismo negocio, el caso más común del padrón— seguían
 * cayendo juntas al separarlas, y encima quedaban TILDADAS: el gesto para deshacer el merge
 * terminaba aprobándolo. Por eso el nombre se desambigua (ver `availableName`).
 */
function setGrouping(state, grouping) {
    const split = grouping === GROUPING_ONE_PER_ACCOUNT;
    const pks = Object.keys(state.decisions.byAccount);
    const moving = pks.filter(pk => movedByTheToggle(state.decisions.byAccount[pk]));
    const isMoving = new Set(moving);

    // Los nombres ya ocupados por destinos que el toggle NO toca: una cuenta separada tampoco puede
    // caer encima de un cliente que el usuario armó a mano.
    const taken = takenNamesOf(state.decisions.byAccount, isMoving);

    const byAccount = {};
    pks.forEach(pk => { byAccount[pk] = state.decisions.byAccount[pk]; });

    // En el orden de las claves para que separar, reagrupar y volver a separar dé siempre lo mismo.
    moving.forEach(pk => {
        const decision = state.decisions.byAccount[pk];
        if (!split) {
            byAccount[pk] = {...decision, clientRef: {type: 'new', name: decision.proposedName},
                             splitName: null, include: false};
            return;
        }
        const name = availableName(decision.soloName, taken);
        taken.add(name);
        // `splitName` se guarda para poder RECONOCER después la cuenta que movió el toggle: si el
        // nombre se desambiguó, ya no coincide con `soloName` y sin esto reagrupar la dejaba afuera.
        //
        // ⚠️ Separar NO tilda. El toggle es un gesto sobre la AGRUPACIÓN —«mi agencia tiene una
        // cuenta por cliente»—, no sobre qué se va a crear: con `include: true` un solo clic dejaba
        // cientos de clientes elegidos, que es exactamente lo que el default destildado vino a
        // evitar («te va a mandar a procesar un montón de cuentas que no me interesan»). Nace
        // `unconfirmed` como todo lo demás, así que la fila ofrece «Incluir igual» y no «Deshacer».
        byAccount[pk] = {...decision, clientRef: {type: 'new', name}, splitName: name,
                         include: false, unconfirmed: true};
    });

    return {...state, grouping, decisions: {...state.decisions, byAccount}};
}

/**
 * ¿Esta cuenta la mueve el toggle?
 *
 * Sólo las que siguen donde las puso Dashbo: ni lo que el usuario armó a mano, ni las asignaciones a
 * clientes que ya existen, ni los clientes de una sola cuenta.
 */
function movedByTheToggle(decision) {
    // Lo que el usuario SACÓ no lo mueve el toggle: reagrupar o separar no puede devolver a la
    // cartera algo que él eliminó. Sin este guard, la cuenta descartada volvía con `include: true`
    // y `dismissed: true` a la vez — invisible en pantalla y creada igual por el POST.
    if (decision.dismissed) return false;
    const isProposedGroup = decision.clientRef?.type === 'new'
        && (decision.groupSize || 1) >= 2
        && Boolean(decision.soloName);
    if (!isProposedGroup) return false;
    return decision.clientRef.name === decision.proposedName
        || decision.clientRef.name === decision.soloName
        || decision.clientRef.name === decision.splitName;
}

/**
 * El nombre libre más parecido al que se pidió.
 *
 * Separar dos cuentas cuyo nombre sugerido es el MISMO no se puede hacer conservándolo: el destino
 * se identifica por el nombre, así que las dos vuelven al mismo cliente. El sufijo numérico es el
 * mismo desempate que ya usa el backend cuando dos líneas se llamarían igual, y acá tiene la ventaja
 * de que se corrige en un clic: el nombre del cliente es editable en la misma fila.
 *
 * **El resultado entra en `CLIENT_NAME_MAX_LENGTH`**, recortando la base si hace falta para que el
 * sufijo quepa. `AgencyClient._name` es `CharField(50)` y `add-bulk` rebota con
 * `CLIENT_NAME_TOO_LONG`: sin esto, separar dos cuentas de nombre largo e idéntico produce un 422
 * sobre una tarjeta cuyo nombre **el usuario nunca escribió**, que es el peor mensaje de error
 * posible. El input del rename ya corta en 50; acá el nombre lo derivamos nosotros, así que recortar
 * está del lado correcto de la regla del repo (el nombre que eligió una persona no se trunca; el que
 * generamos, sí).
 */
export function availableName(wanted, taken) {
    if (!taken.has(wanted) && wanted.length <= CLIENT_NAME_MAX_LENGTH) return wanted;

    let attempt = taken.has(wanted) ? 2 : 1;
    for (;;) {
        const candidate = attempt === 1 ? trimmedToFit(wanted, '') : trimmedToFit(wanted, ` (${attempt})`);
        if (!taken.has(candidate)) return candidate;
        attempt += 1;
    }
}

/** `base` recortada para que `base + suffix` entre en el largo que acepta el backend. */
function trimmedToFit(base, suffix) {
    const room = CLIENT_NAME_MAX_LENGTH - suffix.length;
    return `${base.length > room ? base.slice(0, room).trimEnd() : base}${suffix}`;
}

/** Cuántas cuentas quedaron de cada lado. Es el termómetro del trabajo que falta. */
export function summaryOf(state) {
    const decisions = Object.values(state.decisions.byAccount);
    return {
        total: decisions.length,
        included: decisions.filter(each => each.include && each.clientRef).length,
        doubts: decisions.filter(each => each.isDoubt && each.doubtAnswer === undefined).length,
        unassigned: decisions.filter(each => !each.clientRef && !each.dismissed).length,
        dismissed: decisions.filter(each => each.dismissed).length,
    };
}

/** Los clientes que el usuario va a crear, para poder ofrecerlos como destino de otra cuenta. */
export function clientsInProgress(state) {
    const names = new Set();
    Object.values(state.decisions.byAccount).forEach(decision => {
        if (decision.include && decision.clientRef?.type === 'new') {
            names.add(state.decisions.clientRenames[decision.clientRef.name] || decision.clientRef.name);
        }
    });
    return Array.from(names);
}
